import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  UpOutlined,
} from '@ant-design/icons';
import {
  App,
  Button,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Image,
  Input,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  TreeSelect,
  Typography,
} from 'antd';
import type { TablePaginationConfig } from 'antd/es/table';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Key } from 'react';
import { TASK_PRIORITY_LABELS, TASK_VERIFICATION_LABELS } from '@/constants/task';
import { ApiError } from '@/services/api/client';
import * as departmentsApi from '@/services/api/departments';
import * as tasksApi from '@/services/api/tasks';
import { fetchImageContentUrl } from '@/services/api/uploads';
import type { DepartmentTreeNode } from '@/types/department';
import type {
  CreateTaskBatchPayload,
  TaskDetail,
  TaskGroup,
  TaskListItem,
  TaskPriority,
  TaskVerificationStatus,
  TaskWeeklyReport,
} from '@/types/task';
import { flattenDepartmentTree, toParentTreeSelectData } from '@/utils/department-tree';
import { formatDateTime } from '@/utils/format-date';
import './TaskManagementPage.css';

const { RangePicker } = DatePicker;

interface FilterValues {
  keyword?: string;
  departmentId?: string;
  ownerId?: string;
  dueRange?: [Dayjs, Dayjs];
}

type CompletionFilter = 'ALL' | 'COMPLETED' | 'INCOMPLETE' | 'PENDING_REVIEW';

const COMPLETION_FILTER_OPTIONS: Array<{ label: string; value: CompletionFilter }> = [
  { label: '全部', value: 'ALL' },
  { label: '已完成', value: 'COMPLETED' },
  { label: '未完成', value: 'INCOMPLETE' },
  { label: '待验收', value: 'PENDING_REVIEW' },
];

interface CreateValues {
  title: string;
  content: string;
  ownerIds: string[];
  dueAt: Dayjs;
  priority: TaskPriority;
}

const PRIORITY_OPTIONS = Object.entries(TASK_PRIORITY_LABELS).map(([value, label]) => ({
  value,
  label,
}));

function verificationTag(status: TaskVerificationStatus, taskStatus: TaskListItem['status']) {
  if (taskStatus === 'CANCELLED') {
    return <Tag>已取消</Tag>;
  }
  const colorMap: Record<TaskVerificationStatus, string> = {
    NOT_SUBMITTED: 'orange',
    PENDING_REVIEW: 'blue',
    VERIFIED_COMPLETED: 'green',
    VERIFIED_INCOMPLETE: 'red',
  };
  return <Tag color={colorMap[status]}>{TASK_VERIFICATION_LABELS[status]}</Tag>;
}

function textOrDash(value: string | null | undefined) {
  return value?.trim() || '—';
}

export function TaskManagementPage() {
  const { message, modal } = App.useApp();
  const [filterForm] = Form.useForm<FilterValues>();
  const [createForm] = Form.useForm<CreateValues>();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<TaskGroup[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<readonly Key[]>([]);
  const listRequest = useRef(0);
  const detailRequest = useRef(0);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<FilterValues>({});
  const [completionFilter, setCompletionFilter] = useState<CompletionFilter>('ALL');
  const [departments, setDepartments] = useState<DepartmentTreeNode[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<TaskDetail | null>(null);
  const [detailImageUrls, setDetailImageUrls] = useState<Record<string, string>>({});
  const [weeklyReports, setWeeklyReports] = useState<TaskWeeklyReport[]>([]);
  const [weeklyReportTotal, setWeeklyReportTotal] = useState(0);
  const [weeklyReportPage, setWeeklyReportPage] = useState(0);
  const [loadingWeeklyReports, setLoadingWeeklyReports] = useState(false);
  const imageUrlsRef = useRef<Record<string, string>>({});
  const [editingProgress, setEditingProgress] = useState(false);
  const [progressDraft, setProgressDraft] = useState('');
  const [savingProgress, setSavingProgress] = useState(false);

  const flatDepartments = useMemo(() => flattenDepartmentTree(departments), [departments]);
  const departmentTreeData = useMemo(() => toParentTreeSelectData(departments), [departments]);
  const people = useMemo(() => {
    const seen = new Set<string>();
    return flatDepartments.flatMap((department) =>
      department.users
        .filter((user) => user.role !== 'ADMIN' && !seen.has(user.id))
        .map((user) => {
          seen.add(user.id);
          return {
            value: user.id,
            label: `${user.name}（${department.name}）`,
            departmentName: department.name,
          };
        }),
    );
  }, [flatDepartments]);
  const groupedPeople = useMemo(
    () =>
      flatDepartments
        .map((department) => ({
          label: department.name,
          options: department.users
            .filter((user) => user.role !== 'ADMIN')
            .map((user) => ({ value: user.id, label: `${user.name}（${user.phone}）` })),
        }))
        .filter((group) => group.options.length > 0),
    [flatDepartments],
  );

  const loadDepartments = useCallback(async () => {
    try {
      setDepartments(await departmentsApi.fetchDepartmentTree());
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载部门人员失败';
      message.error(text);
    }
  }, [message]);

  const loadTasks = useCallback(async () => {
    const request = ++listRequest.current;
    setLoading(true);
    try {
      const result = await tasksApi.fetchTaskGroups({
        page,
        pageSize,
        keyword: filters.keyword?.trim() || undefined,
        departmentId: filters.departmentId,
        ownerId: filters.ownerId,
        completionState: completionFilter === 'ALL' ? undefined : completionFilter,
        dueAfter: filters.dueRange?.[0]?.startOf('day').toISOString(),
        dueBefore: filters.dueRange?.[1]?.endOf('day').toISOString(),
      });
      if (request !== listRequest.current) return;
      const lastPage = Math.max(1, Math.ceil(result.total / pageSize));
      if (page > lastPage) {
        setPage(lastPage);
        return;
      }
      setItems(result.items);
      setTotal(result.total);
    } catch (error) {
      if (request !== listRequest.current) return;
      const text = error instanceof ApiError ? error.message : '加载任务失败';
      message.error(text);
    } finally {
      if (request === listRequest.current) setLoading(false);
    }
  }, [completionFilter, filters, message, page, pageSize]);

  useEffect(() => {
    void loadDepartments();
  }, [loadDepartments]);

  useEffect(() => {
    void loadTasks();
  }, [loadTasks]);

  useEffect(() => () => {
    Object.values(imageUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const clearDetailImages = () => {
    Object.values(imageUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    imageUrlsRef.current = {};
    setDetailImageUrls({});
  };

  const loadWeeklyReports = async (id: string, nextPage: number, request: number) => {
    setLoadingWeeklyReports(true);
    try {
      const result = await tasksApi.fetchTaskWeeklyReports(id, { page: nextPage, pageSize: 10 });
      if (request !== detailRequest.current) return;
      setWeeklyReports((previous) => nextPage === 1 ? result.items : [...previous, ...result.items]);
      setWeeklyReportTotal(result.total);
      setWeeklyReportPage(nextPage);
      const loaded = await Promise.all(result.items.flatMap((report) => report.images).map(async (image) => {
        try { return [image.id, await fetchImageContentUrl(image.id)] as const; }
        catch { return [image.id, ''] as const; }
      }));
      if (request !== detailRequest.current) {
        loaded.forEach(([, url]) => { if (url) URL.revokeObjectURL(url); });
        return;
      }
      Object.assign(imageUrlsRef.current, Object.fromEntries(loaded.filter(([, url]) => Boolean(url))));
      setDetailImageUrls({ ...imageUrlsRef.current });
    } catch (error) {
      if (request === detailRequest.current) {
        message.error(error instanceof ApiError ? error.message : '加载周进展记录失败');
      }
    } finally {
      if (request === detailRequest.current) setLoadingWeeklyReports(false);
    }
  };

  const openDetail = async (id: string, editProgress = false) => {
    const request = ++detailRequest.current;
    clearDetailImages();
    setDetail(null);
    setWeeklyReports([]);
    setWeeklyReportTotal(0);
    setWeeklyReportPage(0);
    setEditingProgress(false);
    setProgressDraft('');
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const result = await tasksApi.fetchTask(id);
      if (request === detailRequest.current) {
        setDetail(result);
        setProgressDraft(result.progressContent ?? '');
        setEditingProgress(editProgress && result.status !== 'CANCELLED');
      }
      if (request !== detailRequest.current) return;
      const loadedImages = await Promise.all((result.images ?? []).map(async (image) => {
        try {
          return [image.id, await fetchImageContentUrl(image.id)] as const;
        } catch {
          return [image.id, ''] as const;
        }
      }));
      if (request !== detailRequest.current) {
        loadedImages.forEach(([, url]) => { if (url) URL.revokeObjectURL(url); });
        return;
      }
      const urls = Object.fromEntries(loadedImages.filter(([, url]) => Boolean(url)));
      imageUrlsRef.current = urls;
      setDetailImageUrls(urls);
      await loadWeeklyReports(id, 1, request);
    } catch (error) {
      if (request !== detailRequest.current) return;
      const text = error instanceof ApiError ? error.message : '加载任务详情失败';
      message.error(text);
      setDetail(null);
    } finally {
      if (request === detailRequest.current) setDetailLoading(false);
    }
  };

  const saveProgress = async () => {
    if (!detail || savingProgress) return;
    const content = progressDraft.trim();
    if (!content) {
      message.warning('请输入当前进展');
      return;
    }
    if (content.length > 2000) {
      message.warning('当前进展不能超过2000字');
      return;
    }
    setSavingProgress(true);
    try {
      const updated = await tasksApi.updateTaskProgress(detail.id, content);
      setDetail(updated);
      setProgressDraft(updated.progressContent ?? '');
      setEditingProgress(false);
      message.success('任务进展已更新');
      await loadTasks();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : '保存进展失败，请稍后重试');
    } finally {
      setSavingProgress(false);
    }
  };

  const handleCreate = async (values: CreateValues) => {
    setCreating(true);
    try {
      const payload: CreateTaskBatchPayload = {
        title: values.title.trim(),
        content: values.content.trim(),
        ownerIds: values.ownerIds,
        dueAt: values.dueAt.toISOString(),
        priority: values.priority,
      };
      const result = await tasksApi.createTaskBatch(payload);
      message.success(`已向 ${result.total} 人派发任务`);
      setCreateOpen(false);
      createForm.resetFields();
      if (page === 1) await loadTasks();
      else setPage(1);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '任务派发失败';
      message.error(text);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (task: TaskListItem) => {
    await modal.confirm({
      title: '确认删除该人员的任务',
      content: (
        <div style={{ overflowWrap: 'anywhere' }}>
          <Typography.Paragraph>确定删除「{task.title}」吗？</Typography.Paragraph>
          <Typography.Paragraph>
            接收人：{task.owner?.name ?? '未指定'}
            {task.owner?.department?.name ? `（${task.owner.department.name}）` : ''}
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary">
            仅删除此人的任务，其他人员不受影响，历史办理记录保留。
          </Typography.Paragraph>
        </div>
      ),
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await tasksApi.deleteTask(task.id);
        } catch (error) {
          message.error(error instanceof ApiError ? error.message : '删除任务失败，请稍后重试');
          throw error;
        }
        message.success('任务已删除');
        if (detail?.id === task.id) {
          setDetailOpen(false);
          setDetail(null);
        }
        await loadTasks();
      },
    });
  };

  const verify = async (result: 'COMPLETED' | 'INCOMPLETE') => {
    if (!detail) return;
    let note = '';
    await modal.confirm({
      title: result === 'COMPLETED' ? '确认任务已完成' : '确认任务未完成',
      content: (
        <div style={{ marginTop: 16 }}>
          <Typography.Paragraph type="secondary">
            {result === 'COMPLETED'
              ? '确认后该任务将计入已完成。'
              : '请填写实地核验情况，任务将退回接收人继续办理。'}
          </Typography.Paragraph>
          <Input.TextArea
            rows={4}
            maxLength={500}
            showCount
            placeholder={result === 'COMPLETED' ? '核验说明（选填）' : '请输入未完成原因'}
            onChange={(event) => {
              note = event.target.value;
            }}
          />
        </div>
      ),
      okText: result === 'COMPLETED' ? '确认完成' : '确认未完成',
      okType: result === 'COMPLETED' ? 'primary' : 'danger',
      cancelText: '取消',
      onOk: async () => {
        if (result === 'INCOMPLETE' && !note.trim()) {
          message.warning('请填写未完成原因');
          return Promise.reject(new Error('verification note is required'));
        }
        try {
          const updated = await tasksApi.verifyTask(detail.id, {
            result,
            note: note.trim() || undefined,
          });
          setDetail(updated);
          message.success(result === 'COMPLETED' ? '已确认完成' : '已退回继续办理');
          await loadTasks();
        } catch (error) {
          message.error(error instanceof ApiError ? error.message : '核验失败，请稍后重试');
          throw error;
        }
      },
    });
  };

  const memberColumns: ColumnsType<TaskListItem> = [
    {
      title: '接收人',
      key: 'owner',
      width: 180,
      render: (_, record) => (
        <div>
          <div>{record.owner?.name ?? '—'}</div>
          <Typography.Text type="secondary">{record.owner?.department?.name ?? '—'}</Typography.Text>
        </div>
      ),
    },
    {
      title: '截止日期',
      dataIndex: 'dueAt',
      width: 170,
      render: (value: string, record) => (
        <Space direction="vertical" size={4}>
          <span>{formatDateTime(value)}</span>
          {record.overdue ? <Tag color="red">逾期</Tag> : null}
        </Space>
      ),
    },
    {
      title: '申请核验时间',
      dataIndex: 'completedAt',
      width: 180,
      render: (value: string | null) => value ? formatDateTime(value) : '—',
    },
    {
      title: '当前进展',
      dataIndex: 'progressContent',
      render: textOrDash,
    },
    {
      title: '状态',
      key: 'verificationStatus',
      width: 120,
      render: (_, record) => verificationTag(record.verificationStatus, record.status),
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 245,
      render: (_, record) => (
        <Space size={4}>
          <Button type="link" size="small" onClick={() => void openDetail(record.id)}>
            查看
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} disabled={record.status === 'CANCELLED'}
            onClick={() => void openDetail(record.id, true)}>
            修改进展
          </Button>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => void handleDelete(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const toggleGroup = (key: string) => {
    setExpandedKeys((keys) => keys.includes(key) ? keys.filter((value) => value !== key) : [...keys, key]);
  };

  const columns: ColumnsType<TaskGroup> = [
    {
      title: '任务名称',
      key: 'title',
      width: 340,
      render: (_, group) => (
        <div>
          <Button type="link" className="task-name-link" onClick={() => toggleGroup(group.key)}>
            {group.title}
          </Button>
          {group.members[0]?.project?.name ? (
            <div className="task-secondary">{group.members[0].project.name}</div>
          ) : null}
        </div>
      ),
    },
    {
      title: '涉及人员',
      key: 'recipients',
      width: 280,
      render: (_, group) => {
        const recipients = [...new Map(group.members.map((member) => [member.ownerId, member.owner])).values()];
        return (
          <div className="task-recipients">
            {recipients.map((owner, index) => (
              <div key={owner?.id ?? index}>
                <span>{owner?.name ?? '未指定'}</span>
                <span className="task-secondary">{owner?.department?.name ? `（${owner.department.name}）` : ''}</span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: '截止日期',
      key: 'dueAt',
      width: 175,
      render: (_, group) => {
        const dates = [...new Set(group.members.map((member) => member.dueAt))].sort();
        return (
          <div className="task-date">
            <div>{formatDateTime(dates[0])}</div>
            {dates.length > 1 ? <div className="task-secondary">至 {formatDateTime(dates[dates.length - 1])}</div> : null}
            {group.members.some((member) => member.overdue) ? <Tag color="red">有逾期</Tag> : null}
          </div>
        );
      },
    },
    {
      title: '完成情况',
      key: 'completion',
      width: 165,
      render: (_, group) => {
        const active = group.members.filter((member) => member.status !== 'CANCELLED');
        const completed = active.filter((member) => member.verificationStatus === 'VERIFIED_COMPLETED').length;
        const pending = active.filter((member) => member.verificationStatus === 'PENDING_REVIEW').length;
        const isCompleted = active.length > 0 && completed === active.length;
        return (
          <Space direction="vertical" size={4}>
            <Tag color={active.length === 0 ? undefined : isCompleted ? 'green' : pending > 0 ? 'blue' : 'orange'}>
              {active.length === 0 ? '已取消' : isCompleted ? '已完成' : pending > 0 ? '待验收' : '未完成'}
            </Tag>
            <span>已完成 {completed}/{active.length}</span>
            {pending > 0 ? <span className="task-secondary">待验收 {pending}</span> : null}
            {active.length < group.members.length ? <span className="task-secondary">已取消 {group.members.length - active.length}</span> : null}
          </Space>
        );
      },
    },
    {
      title: '操作', key: 'actions', width: 120, fixed: 'right',
      render: (_, group) => (
        <Button type="link" size="small" icon={expandedKeys.includes(group.key) ? <UpOutlined /> : <DownOutlined />}
          onClick={() => toggleGroup(group.key)}>
          {expandedKeys.includes(group.key) ? '收起人员' : '查看人员'}
        </Button>
      ),
    },
  ];

  const pagination: TablePaginationConfig = {
    current: page,
    pageSize,
    total,
    showSizeChanger: true,
    showTotal: (count) => `共 ${count} 个任务`,
    onChange: (nextPage, nextPageSize) => {
      setPage(nextPage);
      setPageSize(nextPageSize);
    },
  };

  return (
    <div className="task-management">
      <Typography.Title level={3} style={{ marginTop: 0 }}>任务管理</Typography.Title>

      <div className="task-filters">
        <Form<FilterValues>
          form={filterForm}
          layout="inline"
          onFinish={(values) => {
            setFilters(values);
            setPage(1);
            setExpandedKeys([]);
          }}
        >
          <Form.Item name="keyword" label="任务">
            <Input allowClear placeholder="任务名称" style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="departmentId" label="部门">
            <TreeSelect
              allowClear
              treeDefaultExpandAll
              treeData={departmentTreeData}
              placeholder="全部部门"
              style={{ width: 190 }}
            />
          </Form.Item>
          <Form.Item name="ownerId" label="人员">
            <Select allowClear showSearch optionFilterProp="label" options={people} placeholder="全部人员" style={{ width: 190 }} />
          </Form.Item>
          <Form.Item name="dueRange" label="截止日期">
            <RangePicker />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={() => {
                filterForm.resetFields();
                setFilters({});
                setCompletionFilter('ALL');
                setPage(1);
                setExpandedKeys([]);
              }}>重置</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>下发任务</Button>
            </Space>
          </Form.Item>
        </Form>
      </div>

      <div className="task-list">
        <div className="task-status-filter">
          <Segmented
            aria-label="任务状态筛选"
            options={COMPLETION_FILTER_OPTIONS}
            value={completionFilter}
            onChange={(value) => {
              setCompletionFilter(value as CompletionFilter);
              setPage(1);
              setExpandedKeys([]);
            }}
          />
        </div>
        <Table<TaskGroup>
          rowKey="key"
          loading={loading}
          dataSource={items}
          columns={columns}
          pagination={pagination}
          tableLayout="fixed"
          scroll={{ x: 1120 }}
          expandable={{
            expandedRowKeys: expandedKeys,
            onExpandedRowsChange: setExpandedKeys,
            expandedRowRender: (group) => (
              <Table<TaskListItem>
                className="task-members-table"
                rowKey="id"
                size="small"
                tableLayout="fixed"
                dataSource={group.members}
                columns={memberColumns}
                pagination={false}
                scroll={{ x: 1230 }}
              />
            ),
          }}
        />
      </div>

      <Drawer title="下发任务" width={560} open={createOpen} onClose={() => setCreateOpen(false)} destroyOnClose>
        <Form<CreateValues>
          form={createForm}
          layout="vertical"
          initialValues={{ priority: 'MEDIUM' }}
          onFinish={(values) => void handleCreate(values)}
        >
          <Form.Item name="title" label="任务名称" rules={[{ required: true, message: '请输入任务名称' }, { max: 128 }]}>
            <Input maxLength={128} placeholder="请输入任务名称" />
          </Form.Item>
          <Form.Item name="content" label="任务详情" rules={[{ required: true, message: '请输入任务详情' }]}>
            <Input.TextArea rows={6} maxLength={4000} showCount placeholder="请说明任务要求和验收标准" />
          </Form.Item>
          <Form.Item name="ownerIds" label="接收人员" rules={[{ required: true, message: '请选择接收人员' }]}>
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              options={groupedPeople}
              placeholder="按部门选择相关人员"
              maxTagCount="responsive"
            />
          </Form.Item>
          <Form.Item name="dueAt" label="截止日期" rules={[{ required: true, message: '请选择截止日期' }]}>
            <DatePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} disabledDate={(date) => date.endOf('day').isBefore(dayjs())} />
          </Form.Item>
          <Form.Item name="priority" label="优先级">
            <Select options={PRIORITY_OPTIONS} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={creating}>确认下发</Button>
            <Button onClick={() => setCreateOpen(false)}>取消</Button>
          </Space>
        </Form>
      </Drawer>

      <Drawer title="人员办理详情" width="min(620px, 100vw)" open={detailOpen} loading={detailLoading}
        className="task-detail-drawer" closable={!savingProgress} maskClosable={!savingProgress} keyboard={!savingProgress}
        onClose={() => { if (savingProgress) return; ++detailRequest.current; clearDetailImages(); setDetailOpen(false); }}>
        {detail ? (
          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="任务名称">{detail.title}</Descriptions.Item>
              <Descriptions.Item label="接收人">{detail.owner?.name ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="所属部门">{detail.owner?.department?.name ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="截止日期">{formatDateTime(detail.dueAt)}</Descriptions.Item>
              <Descriptions.Item label="申请核验时间">{detail.completedAt ? formatDateTime(detail.completedAt) : '—'}</Descriptions.Item>
              <Descriptions.Item label="状态">{verificationTag(detail.verificationStatus, detail.status)}</Descriptions.Item>
              <Descriptions.Item label="任务详情"><Typography.Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{detail.content}</Typography.Paragraph></Descriptions.Item>
              <Descriptions.Item label="当前进展">
                {editingProgress ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Input.TextArea aria-label="当前进展" value={progressDraft} onChange={(event) => setProgressDraft(event.target.value)}
                      rows={8} maxLength={2000} showCount disabled={savingProgress} />
                    <Space style={{ marginTop: 16 }}>
                      <Button type="primary" loading={savingProgress} onClick={() => void saveProgress()}>保存进展</Button>
                      <Button disabled={savingProgress} onClick={() => { setEditingProgress(false); setProgressDraft(detail.progressContent ?? ''); }}>取消</Button>
                    </Space>
                  </Space>
                ) : (
                  <>
                    <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{textOrDash(detail.progressContent)}</Typography.Paragraph>
                    <Button type="link" icon={<EditOutlined />} disabled={detail.status === 'CANCELLED'}
                      onClick={() => { setProgressDraft(detail.progressContent ?? ''); setEditingProgress(true); }}>修改进展</Button>
                  </>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="任务总结"><Typography.Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{textOrDash(detail.completionNote)}</Typography.Paragraph></Descriptions.Item>
              {detail.verificationNote ? <Descriptions.Item label="核验说明">{detail.verificationNote}</Descriptions.Item> : null}
              {detail.verifiedAt ? <Descriptions.Item label="核验时间">{formatDateTime(detail.verifiedAt)}</Descriptions.Item> : null}
            </Descriptions>
            {detail.images && detail.images.length > 0 ? (
              <div>
                <Typography.Text strong>进展图片（{detail.images.length}）</Typography.Text>
                <Image.PreviewGroup>
                  <div className="task-detail-images">
                    {detail.images.map((image) => detailImageUrls[image.id] ? (
                      <Image key={image.id} width={112} height={112} style={{ objectFit: 'cover' }}
                        src={detailImageUrls[image.id]} alt={image.originalName} />
                    ) : (
                      <div key={image.id} className="task-detail-image-unavailable">图片暂不可预览</div>
                    ))}
                  </div>
                </Image.PreviewGroup>
              </div>
            ) : null}
            <div className="task-weekly-section">
              <Typography.Text strong>历次周进展（{weeklyReportTotal}）</Typography.Text>
              {weeklyReports.length === 0 && !loadingWeeklyReports ? <div className="task-secondary">暂无周进展记录</div> : null}
              {weeklyReports.map((report) => (
                <div key={report.id} className="task-weekly-entry">
                  <Space wrap>
                    <Typography.Text type="secondary">{formatDateTime(report.submittedAt)}</Typography.Text>
                    <Typography.Text>{report.reporter.name}</Typography.Text>
                    {report.completionRequested ? <Tag color="blue">申请核验</Tag> : null}
                  </Space>
                  <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', margin: '10px 0 0' }}>{report.progressContent}</Typography.Paragraph>
                  {report.images.length > 0 ? (
                    <Image.PreviewGroup>
                      <div className="task-detail-images">
                        {report.images.map((image) => detailImageUrls[image.id] ? (
                          <Image key={image.id} width={88} height={88} style={{ objectFit: 'cover' }}
                            src={detailImageUrls[image.id]} alt={image.originalName} />
                        ) : (
                          <div key={image.id} className="task-detail-image-unavailable">图片暂不可预览</div>
                        ))}
                      </div>
                    </Image.PreviewGroup>
                  ) : null}
                </div>
              ))}
              {weeklyReports.length < weeklyReportTotal ? (
                <Button loading={loadingWeeklyReports} onClick={() => void loadWeeklyReports(detail.id, weeklyReportPage + 1, detailRequest.current)}>
                  加载更多
                </Button>
              ) : null}
            </div>
            {!editingProgress && detail.verificationStatus === 'PENDING_REVIEW' ? (
              <Space>
                <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => void verify('COMPLETED')}>确认完成</Button>
                <Button danger icon={<CloseCircleOutlined />} onClick={() => void verify('INCOMPLETE')}>确认未完成</Button>
              </Space>
            ) : null}
            <Button danger disabled={editingProgress || savingProgress} icon={<DeleteOutlined />} onClick={() => void handleDelete(detail)}>
              删除该人员任务
            </Button>
          </Space>
        ) : null}
      </Drawer>
    </div>
  );
}
